// One description of every employee detail, used by BOTH the Add/Edit page and the profile page,
// so a field is defined once. A field is { path, label, type, ... }:
//   type: text | phone | textarea | select | date | isodate | number | checkbox | money | region | ref:<lookup>
//   sensitive: shown masked on the profile (ID numbers, bank details ...)
// Sections marked `special` are drawn by the page itself (account, assets, next of kin, attachments ...).

const opts = (...labels) => labels.map((l) => ({ value: l, label: l }));

export const OPTIONS = {
  gender: opts("Male", "Female", "Non-binary", "Prefer not to say"),
  maritalStatus: opts("Single", "Married", "Divorced", "Widowed", "Separated"),
  immigrationStatus: opts("Citizen", "Permanent resident", "Work visa", "Dependant visa", "Student visa", "Other"),
  payFrequency: opts("Monthly", "Semi-monthly", "Weekly", "Daily", "Hourly"),
  accountingStatus: opts("Employee", "Contractor", "Director", "Intern"),
  paymentMethod: opts("Bank transfer", "Cheque", "Cash", "Other"),
  cessationReason: opts("Resignation", "Termination", "End of contract", "Retirement", "Redundancy", "Other"),
  voluntaryType: opts("None", "Fixed amount", "Percentage"),
  deductionSetting: opts("Deduct in the pay run", "Pay separately", "Not applicable"),
  vaccination: opts("Not vaccinated", "Partially vaccinated", "Fully vaccinated", "Booster received", "Exempt", "Prefer not to say"),
  workingDay: opts("5 day week", "5.5 day week", "6 day week", "Shift / rotating"),
  attachmentAccess: [
    { value: "hidden", label: "Hidden from the employee" },
    { value: "view", label: "Employee can view" },
  ],
};

export const SECTIONS = {
  personal: {
    title: "Personal details",
    fields: [
      { path: ["firstName"], label: "First name", type: "text", required: true },
      { path: ["lastName"], label: "Last name", type: "text", required: true },
      { path: ["personal", "nameOnId"], label: "Name on ID card / passport", type: "text", help: "Used in tax forms and statutory files. Leave blank to use the name above." },
      { path: ["personal", "birthDate"], label: "Birth date", type: "date" },
      { path: ["personal", "gender"], label: "Gender", type: "select", options: OPTIONS.gender },
      { path: ["personal", "maritalStatus"], label: "Marital status", type: "select", options: OPTIONS.maritalStatus },
      { path: ["personal", "localName"], label: "Name in local language", type: "text", help: "For example the Chinese name" },
      { path: ["personal", "nickname"], label: "Alias", type: "text" },
    ],
  },
  job: {
    title: "Current job details",
    fields: [
      { path: ["employeeId"], label: "Employee ID", type: "text", readOnly: true, editOnly: true },
      { path: ["jobId"], label: "Job ID", type: "text" },
      { path: ["designation"], label: "Title / designation", type: "text" },
      { path: ["department"], label: "Department", type: "ref:departments" },
      { path: ["role"], label: "Role", type: "ref:roles" },
      { path: ["jobStartDate"], label: "Job start date", type: "date" },
      { path: ["jobEndDate"], label: "Job end date", type: "date", help: "Leave empty for an ongoing job" },
      { path: ["accountingStatus"], label: "In accounting software, the person is", type: "select", options: OPTIONS.accountingStatus },
      { path: ["remarks"], label: "Remarks", type: "textarea" },
    ],
  },
  contact: {
    title: "Contact details",
    fields: [
      { path: ["phone"], label: "Contact number", type: "phone" },
      { path: ["region"], label: "Region", type: "region", help: "Country the employee is based in. Used for filtering and for region-wise holidays and policies" },
      { path: ["contact", "state"], label: "State / province", type: "text" },
      { path: ["contact", "addressLine1"], label: "Address line 1", type: "text" },
      { path: ["contact", "addressLine2"], label: "Address line 2", type: "text" },
      { path: ["contact", "postalCode"], label: "Postal code", type: "text" },
    ],
  },
  employment: {
    title: "Employment details",
    fields: [
      { path: ["joiningDate"], label: "Hired date", type: "isodate" },
      { path: ["employment", "confirmationDate"], label: "Confirmation date", type: "date" },
      { path: ["employment", "resignDate"], label: "Resign date", type: "date" },
      { path: ["employment", "cessationReason"], label: "Reason for cessation", type: "select", options: OPTIONS.cessationReason },
      { path: ["employment", "cessationNote"], label: "Cessation note", type: "textarea" },
      { path: ["employment", "isLeavingCountry"], label: "Is leaving the country", type: "checkbox" },
    ],
  },
  schedule: {
    title: "Work schedule",
    fields: [
      { path: ["assignedOffice"], label: "Branch / office", type: "ref:offices", help: "Attendance is checked against this office and its timezone" },
      { path: ["workingHours"], label: "Working hours", type: "ref:workingHours", help: "Leave empty to use the office default" },
      { path: ["workingDayPattern"], label: "Working day", type: "select", options: OPTIONS.workingDay, help: "For records. Attendance currently treats Saturday and Sunday as the weekend" },
    ],
  },
  pay: {
    title: "Pay",
    note: "Visible to admins only",
    fields: [
      { path: ["payFrequency"], label: "Rate of pay", type: "select", options: OPTIONS.payFrequency },
      { path: ["salaryCurrency"], label: "Currency for salary", type: "currency" },
      { path: ["monthlySalary"], label: "Basic salary", type: "money" },
      { path: ["costCentre"], label: "Default cost centre", type: "text" },
    ],
  },
  identification: {
    title: "Identification documents",
    note: "Sensitive",
    fields: [
      { path: ["identification", "immigrationStatus"], label: "Immigration status", type: "select", options: OPTIONS.immigrationStatus, sensitive: false },
      { path: ["identification", "idNumber"], label: "Identification number", type: "text", sensitive: true },
      { path: ["identification", "nationality"], label: "Nationality", type: "region" },
      { path: ["identification", "passportNumber"], label: "Passport number", type: "text", sensitive: true },
      { path: ["identification", "passportIssueDate"], label: "Passport date of issue", type: "date" },
      { path: ["identification", "passportExpiryDate"], label: "Passport date of expiry", type: "date" },
      { path: ["identification", "passportPlaceOfIssue"], label: "Passport place of issue", type: "text" },
    ],
  },
  payment: {
    title: "Payment details",
    note: "Sensitive",
    fields: [
      { path: ["payment", "method"], label: "Payment method", type: "select", options: OPTIONS.paymentMethod },
      { path: ["payment", "bankName"], label: "Bank", type: "text" },
      { path: ["payment", "accountNumber"], label: "Account number (no dashes or spaces)", type: "text", sensitive: true },
      { path: ["payment", "bankCode"], label: "Bank code", type: "text" },
      { path: ["payment", "branchCode"], label: "Branch code", type: "text" },
      { path: ["payment", "accountHolderName"], label: "Bank account holder's name", type: "text" },
      { path: ["payment", "swiftCode"], label: "SWIFT code", type: "text", sensitive: true },
      { path: ["payment", "branchName"], label: "Branch name", type: "text" },
    ],
  },
  statutory: {
    title: "Statutory & pension",
    note: "Sensitive",
    fields: [
      { path: ["statutory", "scheme"], label: "Pension scheme (for example MPF)", type: "text" },
      { path: ["statutory", "employeeType"], label: "Employee type under the scheme", type: "text" },
      { path: ["statutory", "employeeVoluntaryType"], label: "Voluntary employee contribution type", type: "select", options: OPTIONS.voluntaryType },
      { path: ["statutory", "employeeVoluntaryAmount"], label: "Voluntary employee contribution amount", type: "number" },
      { path: ["statutory", "employeeDeductionSetting"], label: "Voluntary employee deduction setting", type: "select", options: OPTIONS.deductionSetting },
      { path: ["statutory", "employerVoluntaryType"], label: "Voluntary employer contribution type", type: "select", options: OPTIONS.voluntaryType },
      { path: ["statutory", "employerVoluntaryAmount"], label: "Voluntary employer contribution amount", type: "number" },
      { path: ["statutory", "employerDeductionSetting"], label: "Voluntary employer deduction setting", type: "select", options: OPTIONS.deductionSetting },
      { path: ["statutory", "memberAccountNumber"], label: "Member account number", type: "text", sensitive: true },
    ],
  },
  health: {
    title: "Health & safety",
    fields: [{ path: ["vaccinationStatus"], label: "Covid-19 vaccination status", type: "select", options: OPTIONS.vaccination }],
  },
  // drawn by the pages themselves
  account: { title: "User account", special: true },
  assets: { title: "Allocated assets", special: true },
  nextOfKin: { title: "Next of kin", special: true },
  customFields: { title: "Custom fields", special: true },
  attachments: { title: "Related attachments", special: true },
};

// Which section sits in which of the three columns (same arrangement as a classic HRMS profile).
export const COLUMNS = [
  ["personal", "employment", "identification", "attachments"],
  ["job", "contact", "statutory", "customFields"],
  ["account", "pay", "payment", "schedule", "assets", "nextOfKin", "health"],
];

export const getIn = (obj, path) => path.reduce((o, k) => (o == null ? undefined : o[k]), obj);

const setIn = (obj, path, value) => {
  let o = obj;
  path.slice(0, -1).forEach((k) => {
    o[k] = o[k] || {};
    o = o[k];
  });
  o[path[path.length - 1]] = value;
};

// Builds the request body from the form values, field by field, so that a cleared field really
// clears it (text -> "", reference -> null), numbers are numbers and checkboxes are true/false.
export const buildPayload = (values, { editing }) => {
  const out = {};
  Object.entries(SECTIONS).forEach(([, section]) => {
    (section.fields || []).forEach((f) => {
      if (f.readOnly || (f.editOnly && !editing)) return;
      const v = getIn(values, f.path);
      let value;
      if (f.type === "checkbox") value = !!v;
      else if (f.type === "number" || f.type === "money") value = Number(v) || 0;
      else if (f.type.startsWith("ref:")) value = v || null;
      else if (f.type === "isodate") value = v || null;
      else value = v === undefined || v === null ? "" : v;
      setIn(out, f.path, value);
    });
  });
  out.allocatedAssets = values.allocatedAssets || [];
  out.nextOfKin = (values.nextOfKin || []).filter((k) => k && (k.name || k.relationship || k.phone)).map((k) => ({ name: k.name || "", relationship: k.relationship || "", phone: k.phone || "" }));
  out.customFields = (values.customFields || []).filter((c) => c && (c.label || c.value)).map((c) => ({ label: c.label || "", value: c.value || "" }));
  out.attachmentAccess = values.attachmentAccess || "hidden";
  return out;
};

export const maskValue = (v) => {
  const s = String(v || "");
  return s.length <= 4 ? "••••" : `${"•".repeat(Math.min(8, s.length - 4))}${s.slice(-4)}`;
};
